import React, { useEffect, useMemo, useState } from 'react';
import { datevApi, formatApiError } from '../services/api';
import { FileText, DownloadSimple, Files, Calendar, Coins } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const fmtEUR = (v) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));

const PERIODS = [
  { key: 'month', label: 'Aktueller Monat' },
  { key: 'quarter', label: 'Aktuelles Quartal' },
  { key: 'year', label: 'Aktuelles Jahr' },
  { key: 'custom', label: 'Freier Zeitraum' },
];

const KINDS = [
  { key: 'all', label: 'Alles zusammen' },
  { key: 'invoices', label: 'Rechnungen' },
  { key: 'payments', label: 'Zahlungseingänge' },
  { key: 'cashbook', label: 'Kassenbuch' },
];

export default function DatevExport() {
  const today = new Date().toISOString().slice(0, 10);
  const [period, setPeriod] = useState('month');
  const [kind, setKind] = useState('all');
  const [fromDate, setFromDate] = useState(today.slice(0, 8) + '01');
  const [toDate, setToDate] = useState(today);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const params = useMemo(() => {
    const p = { period, kind };
    if (period === 'custom') { p.from_date = fromDate; p.to_date = toDate; }
    return p;
  }, [period, kind, fromDate, toDate]);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const r = await datevApi.preview(params);
      setPreview(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadPreview(); }, [period, kind, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="p-6 space-y-6" data-testid="datev-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-[#111418]">DATEV-Export</h1>
        <p className="text-[#525866] mt-1">Buchungsstapel im offiziellen DATEV-EXTF-Format für Ihren Steuerberater.</p>
      </div>

      {/* Konfiguration */}
      <div className="card-swiss p-5 space-y-4" data-testid="datev-config">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="overline">Zeitraum</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
              {PERIODS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPeriod(p.key)}
                  data-testid={`period-${p.key}`}
                  className={`px-3 py-2 rounded-lg border text-sm font-medium transition ${
                    period === p.key ? 'bg-[#111418] text-white border-[#111418]' : 'bg-white text-[#525866] border-[#E1E4E8] hover:bg-[#F5F7FA]'
                  }`}
                >{p.label}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="overline">Buchungsart</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {KINDS.map((k) => (
                <button
                  key={k.key}
                  onClick={() => setKind(k.key)}
                  data-testid={`kind-${k.key}`}
                  className={`px-3 py-2 rounded-lg border text-sm font-medium transition ${
                    kind === k.key ? 'bg-[#111418] text-white border-[#111418]' : 'bg-white text-[#525866] border-[#E1E4E8] hover:bg-[#F5F7FA]'
                  }`}
                >{k.label}</button>
              ))}
            </div>
          </div>
        </div>
        {period === 'custom' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline">Von</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="input-swiss" data-testid="from-date" />
            </div>
            <div>
              <Label className="overline">Bis</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="input-swiss" data-testid="to-date" />
            </div>
          </div>
        )}
      </div>

      {/* Vorschau */}
      <div className="card-swiss p-5" data-testid="datev-summary">
        {loading ? (
          <div className="h-16 skeleton" />
        ) : preview ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <div className="overline flex items-center gap-1"><Calendar weight="bold" size={12}/> Zeitraum</div>
              <p className="text-lg font-bold text-[#111418]">
                {new Date(preview.period_start).toLocaleDateString('de-DE')} – {new Date(preview.period_end).toLocaleDateString('de-DE')}
              </p>
            </div>
            <div>
              <div className="overline flex items-center gap-1"><Files weight="bold" size={12}/> Kontenrahmen</div>
              <p className="text-lg font-bold text-[#111418]">{preview.kontenrahmen}</p>
              <p className="text-xs text-[#8A94A6]">konfigurierbar</p>
            </div>
            <div>
              <div className="overline flex items-center gap-1"><FileText weight="bold" size={12}/> Buchungen</div>
              <p className="text-2xl font-bold text-[#111418]">{preview.rows_count}</p>
            </div>
            <div>
              <div className="overline flex items-center gap-1"><Coins weight="bold" size={12}/> Gesamtvolumen</div>
              <p className="text-2xl font-bold text-[#111418]">{fmtEUR(preview.total_amount)}</p>
            </div>
          </div>
        ) : <div className="text-sm text-[#525866]">Keine Daten geladen.</div>}
      </div>

      {/* Aktionen */}
      <div className="flex gap-3 flex-wrap">
        <a href={datevApi.csvUrl(params)} download data-testid="download-csv-btn">
          <Button className="bg-[#111418] text-white hover:bg-[#000] px-6 py-3" disabled={!preview || preview.rows_count === 0}>
            <DownloadSimple weight="bold" className="mr-2" size={18} /> DATEV-CSV herunterladen (EXTF)
          </Button>
        </a>
        <a href={datevApi.pdfUrl(params)} download data-testid="download-pdf-btn">
          <Button variant="outline" className="px-6 py-3" disabled={!preview || preview.rows_count === 0}>
            <FileText weight="bold" className="mr-2" size={18} /> Übersicht als PDF
          </Button>
        </a>
      </div>

      {/* Preview-Tabelle */}
      {preview && preview.rows_preview?.length > 0 && (
        <div className="card-swiss overflow-hidden" data-testid="preview-table">
          <div className="p-4 border-b border-[#E2E4E9]">
            <h2 className="text-sm font-semibold text-[#111418]">Vorschau (erste 50 Buchungen)</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="table-swiss text-xs">
              <thead><tr>
                <th>Beleg-Datum</th><th>Beleg</th><th>S/H</th><th>Konto</th>
                <th>Gegen-Konto</th><th>BU</th><th className="text-right">Betrag</th><th>Text</th>
              </tr></thead>
              <tbody>
                {preview.rows_preview.map((r, i) => (
                  <tr key={i} data-testid={`preview-row-${i}`}>
                    <td className="font-mono">{r.belegdatum_ddmm}</td>
                    <td className="font-semibold">{r.beleg1}</td>
                    <td>{r.soll_haben}</td>
                    <td className="font-mono">{r.konto}</td>
                    <td className="font-mono">{r.gegenkonto}</td>
                    <td>{r.bu || '—'}</td>
                    <td className="text-right font-semibold">{fmtEUR(r.umsatz)}</td>
                    <td className="text-[#525866]">{r.buchungstext}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="text-xs text-[#8A94A6] p-3 bg-[#FAFAFB] rounded border border-[#E2E4E9]">
        <b>Hinweis:</b> Die CSV-Datei ist im offiziellen DATEV-EXTF-Format (Windows-1252 kodiert) und kann direkt in DATEV Rechnungswesen importiert werden.
        Konten, Kontenrahmen (SKR03/SKR04) sowie Berater- und Mandantennummer sind in den <b>Einstellungen</b> anpassbar (Datev-Bereich).
      </div>
    </div>
  );
}
